/**
 * Стартовый скоуп карты V2 (прод 17.08.2026).
 *
 * Зачем вообще
 * ------------
 * Спутниковые продукты живут ТОЛЬКО в farm-скоупе: кнопка «Снимки» в
 * `RightRail` рендерится под `activeScope.type === 'farm'` (лок founder'а
 * AK-106), а растровые эндпоинты отказывают без `farm_id`
 * (`_reject_non_farm_scope_response`). Дефолт же — company.
 *
 * У большого хозяйства это незаметно: ферму выбирают в навигаторе. А в
 * кабинете клиента ферма ровно одна, выбирать нечего — человек открывает
 * карту, на которой кнопки NDVI нет вообще, хотя снимки посчитаны.
 *
 * Порядок победителей (закреплён тестами)
 * ---------------------------------------
 *   URL (`?farm=`/`?cluster=`, AK-296)
 *     > КОНКРЕТНЫЙ серверный указатель (cluster/farm/brigade из `/scope/available`)
 *       > авто-выбор единственной фермы
 *         > широкий current как есть (company/tenant/holding — см. WIDE_SCOPE_TYPES)
 *
 * URL первый не случайно: расшаренная ссылка обязана открыть свой скоуп, а не
 * чей-то последний выбор. Широкий current (staging 20.08) выбором человека НЕ
 * считается — это дефолт store либо `available[0]`-фоллбэк сервера у сессии
 * без указателя, и авто-выбор единственной фермы его перебивает.
 *
 * Чего эта функция НЕ делает
 * --------------------------
 * Не пишет скоуп на сервер. `POST /scope/set` кладёт значение в
 * `auth.sessions.active_scope_*` — одну колонку на сессию, БЕЗ привязки к
 * тенанту. Консультант в окне клиента получил бы чужой `farm_id` в свою
 * сессию, а после выхода — молча пустые дашборды в своём тенанте
 * (`fields.farm_id = <чужой uuid>`). Поэтому авто-выбор живёт только в
 * map-store, как и остальные переключения скоупа в V2 (`persistScope` там
 * тоже не ходит на сервер — серверный указатель пишет ровно один хозяин,
 * `useScopeSetter` под переключателем «Хозяйство ▾» в сайдбаре).
 *
 * Системная дыра `active_scope` без тенанта — зона Head-Backend, отдельный
 * тикет; здесь мы просто не делаем её сценарием по умолчанию.
 */
import type { ActiveScope } from './useMapStore';

/** Что реально отдаёт `/api/v1/scope/available` (backend `ScopeInfo`). */
export type ScopeInfoType = 'tenant' | 'holding' | 'company' | 'cluster' | 'farm' | 'brigade';

/**
 * «Широкие» скоупы — вся земля кабинета, а не конкретное хозяйство.
 *
 * Для них backend `build_scope_filter` — no-op (фильтра по fields нет), и
 * главное — такой `current` НЕ доказывает выбор человека: у свежей сессии
 * указатель `auth.sessions.active_scope_*` пуст (а `select-tenant` из #800 его
 * ещё и обнуляет при смене кабинета), и бэкендовский `_find_current()` отдаёт
 * `available[0]` — а первым в списке идёт ХОЛДИНГ, сид-строка которого есть в
 * каждом кабинете (`proto_tenant_service` сеет holding раньше company).
 * Дефолт сервера — не выбор; авто-подбор единственной фермы для широких
 * скоупов легитимен так же, как для company (#782).
 *
 * cluster/farm/brigade в этот список НЕ входят: это конкретика, которую мог
 * выбрать только человек (или ссылка) — её не перебиваем.
 */
const WIDE_SCOPE_TYPES: ReadonlySet<ScopeInfoType> = new Set(['tenant', 'holding', 'company']);

export interface ScopeRef {
  scope_type: ScopeInfoType;
  scope_id: string | null;
}

export interface InitialScopeInput {
  /** Скоуп из URL — приоритетный вход (AK-296). */
  urlScope: ActiveScope | null;
  /** Указатель сервера из `/scope/available`; null — ответ не пришёл. */
  current: ScopeRef | null;
  /** Доступные скоупы из того же ответа (гонки нет — один запрос). */
  available?: ScopeRef[] | null;
}

export interface InitialScopeResult {
  /** Что положить в store; null — ставить нечего (ответ не пришёл, URL пуст). */
  scope: ActiveScope | null;
  /**
   * Ферма выбрана НАМИ, а не человеком/сервером/ссылкой.
   *
   * Явный флаг вместо сравнения ссылок на объект: `target !== current`
   * держалось бы на идентичности и однажды сломалось бы от безобидного
   * `{...current}` внутри функции.
   */
  autoSelectedFarm: boolean;
}

export function resolveInitialScope(input: InitialScopeInput): InitialScopeResult {
  const { urlScope, current, available } = input;

  // 1. Ссылка сильнее всего.
  if (urlScope) return { scope: urlScope, autoSelectedFarm: false };

  // 2. Сервер не ответил — ставить нечего, карта останется на дефолте store.
  if (!current) return { scope: null, autoSelectedFarm: false };

  // 3. Человек уже выбирал что-то КОНКРЕТНОЕ (cluster/farm/brigade) — не
  //    перебиваем. Широкий current (company/tenant/holding) выбором не
  //    считается: это либо дефолт store, либо `available[0]`-фоллбэк сервера
  //    у сессии без указателя (см. WIDE_SCOPE_TYPES).
  if (!WIDE_SCOPE_TYPES.has(current.scope_type)) {
    return { scope: { type: current.scope_type, id: current.scope_id }, autoSelectedFarm: false };
  }

  // 4. Ферма ровно одна — двусмысленности нет, выбираем её.
  const farms = (available ?? []).filter((s) => s.scope_type === 'farm' && s.scope_id);
  if (farms.length === 1) {
    return { scope: { type: 'farm', id: farms[0].scope_id }, autoSelectedFarm: true };
  }

  // 5. Иначе — как было.
  return { scope: { type: current.scope_type, id: current.scope_id }, autoSelectedFarm: false };
}

/**
 * Второй пояс для ОКНА КЛИЕНТА (прод 19.08.2026, v1.1705).
 *
 * Почему первого пояса не хватает
 * -------------------------------
 * `resolveInitialScope` выбирает единственную ферму из `available[]` ответа
 * `/scope/available`. Но в окне клиента (`enter-client`, viewing-токен) у
 * консультанта НЕТ строки в `auth.tenant_members` тенанта клиента — доступ
 * идёт через орг-грант (RLS `app.viewing_grant_id`), а `/scope/available`
 * строит список ТОЛЬКО по memberships. Пустые memberships → пустой список →
 * HTTPException(403) → внешний catch эндпоинта возвращает stub
 * `{company, null, «Загрузка данных…»}` БЕЗ ферм. Авто-выбор #782 срабатывать
 * не на чем — карта навсегда в company-скоупе, кнопки «Снимки» нет (лок
 * AK-106), available-dates даже не запрашивается.
 *
 * Что делаем
 * ----------
 * Если после первого пояса скоуп остался company И сервер не назвал ни одной
 * фермы — спрашиваем фермы напрямую у `GET /api/v1/farms` (он ходит через
 * `get_tenant_db`, effective = viewing OR active, т.е. в окне клиента честно
 * отвечает фермами клиента — тем же путём живёт AddFieldPanel). Ровно одна —
 * выбираем её (двусмысленности нет). Несколько или ноль — прежнее поведение.
 *
 * Для обычного кабинета фоллбэк мёртв по построению: company-visibility
 * пользователю `/scope/available` всегда перечисляет фермы, а
 * cluster/farm-вайтлистовому `current` не company. Лишнего запроса нет.
 *
 * Как и первый пояс — НИЧЕГО не пишет на сервер (никакого `/scope/set`):
 * выбор живёт только в map-store. Причина — кросс-тенантная утечка
 * `active_scope_*`, см. шапку модуля.
 */
export async function resolveInitialScopeWithFarmFallback(
  input: InitialScopeInput,
  fetchFarms: () => Promise<Array<{ id: string }>>,
): Promise<InitialScopeResult> {
  const first = resolveInitialScope(input);

  // Фоллбэк уместен только когда никто ничего не выбрал: не ссылка (AK-296),
  // не человек/сервер, не сам первый пояс.
  //
  // «Никто не выбрал» — это и `scope === null` (staging 20.08: `/scope/available`
  // не ответил вовсе → current=null; старый гард `?.type !== 'company'` на
  // undefined выключал пояс ровно в окне клиента, для которого тот написан), и
  // широкий дефолт (company/tenant/holding — см. WIDE_SCOPE_TYPES).
  const nobodyChose = first.scope === null || WIDE_SCOPE_TYPES.has(first.scope.type as ScopeInfoType);
  if (input.urlScope || first.autoSelectedFarm || !nobodyChose) {
    return first;
  }
  // Сервер назвал хоть одну ферму → первый пояс уже принял решение по полному
  // списку (несколько ферм = выбор за человеком). Не перебиваем.
  const serverNamedFarms = (input.available ?? []).some((s) => s.scope_type === 'farm' && s.scope_id);
  if (serverNamedFarms) return first;

  let farms: Array<{ id: string }>;
  try {
    farms = await fetchFarms();
  } catch {
    // Фоллбэк — не гейт: карта не должна умереть из-за него.
    return first;
  }
  if (farms.length === 1 && farms[0]?.id) {
    return { scope: { type: 'farm', id: farms[0].id }, autoSelectedFarm: true };
  }
  return first;
}
